import {FormattedMessage} from 'react-intl';

import {useRef, useState} from 'react';

import {Button} from '@time-webkit/all/atoms/button';
import {Notification as Alert, NotificationType} from '@time-webkit/all/atoms/notification';

import * as Utils from 'utils/utils';

import {useNotificationPermission, usePushSubscription} from 'features/push_notifications';

import {reportErrorToSentry} from 'utils/sentry';

import {type AsyncResult} from 'utils/async_result';

import styles from './styles.module.css';

const PermissionsAlert: React.FC = () => {
    const {granted, canUsePermission, requestPermission} = useNotificationPermission();
    const {subscribed, canSubscribe, subscribe} = usePushSubscription({withGuide: true});
    const [isInProgress, setInProgress] = useState(false);
    const wasRequestedOnceRef = useRef(false);

    const handleRequestPermission = async () => {
        setInProgress(true);
        let result: AsyncResult<unknown>;
        if (canSubscribe) {
            result = await subscribe();
        } else {
            result = await requestPermission();
        }
        wasRequestedOnceRef.current = true;
        const [, error] = result;
        if (error) {
            reportErrorToSentry(error);
        }
        setInProgress(false);
    };

    const content = (
        <div className={styles.alertContainer}>
            <div>
                <FormattedMessage
                    id='user.settings.notifications.noPermission.message'
                    defaultMessage="You won't miss new messages. If the notifications are not coming, check the Settings section of your phone"
                />
            </div>
            <div>
                <Button onPress={handleRequestPermission} loading={isInProgress}>
                    <FormattedMessage
                        id='user.settings.notifications.noPermission.request'
                        defaultMessage='Allow'
                    />
                </Button>
            </div>
        </div>
    );

    // если браузер не поддерживает Notfications API, кнопку не нужно показывать
    if (!canUsePermission) {
        return null;
    }

    // если PushAPI активирован
    if (subscribed) {
        return null;
    }

    // если разрешены уведомления и PushAPI нельзя активировать
    if (granted && !canSubscribe) {
        return null;
    }

    if (wasRequestedOnceRef.current) {
        return null;
    }

    return (
        <div className={styles.container}>
            <Alert
                type={NotificationType.Info}
                title={Utils.localizeMessage(
                    'user.settings.notifications.noPermission.title',
                    'Allow notification',
                )}
                content={content}
            />
        </div>
    );
};

export default PermissionsAlert;
